import React, { useEffect, useRef, useState, KeyboardEvent } from "react";
import {
  Zap,
  User,
  Bot,
  Sparkles,
  Brain,
  Clock,
  Building,
  Package2,
  ClipboardList,
  SquareCheckBig,
  ChevronDown,
  ChevronUp,
  Loader2,
  AlertCircle,
  Wrench,
} from "lucide-react";
import { CheckCircle2, Circle, XCircle } from "lucide-react";
import {
  AGENT_ACTIONS_BASE_URL,
  INTEGRATION_RESOURCES_UPLOAD,
} from "../../constants/apiUrl";
import { ScrollArea } from "../../components/UI/ScrollArea";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../../components/UI/Tabs";
import { getCookie } from "../../helpers/jwt";
import { ACCESS_TOKEN } from "../../constants/constants";
import { v4 as uuidv4 } from "uuid";
import {
  DATE_TIME_FORMAT_WITH_SECONDS,
  base64UrlToUuid,
} from "../../helpers/shared";
import { DateTime } from "luxon";
import axios from "axios";
import { useSearchParams } from "react-router";
// import AgentExecutionTraceDetails from '~/components/AgentSDK/AgentExecutionTraceDetails';

// Hardcoded URL from user request
const encodeAgentInputs = encodeURIComponent(JSON.stringify({}));

const stepsMapping = {
  crew_kickoff_started: {
    title: "Connecting to Agent",
    description: "Initiating communication...",
    icon: <Building className="h-4 w-4 text-blue-600" />,
  },
  task_started: {
    title: "Collecting information",
    description: "Researching on topic...",
    icon: <Package2 className="h-4 w-4 text-orange-500" />,
  },
  agent_execution_started: {
    title: "Connected to Agent",
    description: "Connected to Agent and analyzing request...",
    icon: <ClipboardList className="h-4 w-4 text-purple-500" />,
  },
  // llm_call_started: {
  //     title: 'Looking for the right tool',
  //     description: 'Sending a request to the right tool...',
  //     icon: <Brain className="h-4 w-4 text-pink-500" />,
  // },
  final_response: {
    title: "Finalizing",
    description: "Preparing final answer...",
    icon: <SquareCheckBig className="h-4 w-4 text-green-600" />,
  },
  agent_execution_error: {
    title: "Execution Error",
    description: "An error occurred during execution",
    icon: <AlertCircle className="h-4 w-4 text-red-600" />,
  },
  // tool_usage_started: {
  //     title: 'Using Tool',
  //     description: 'Executing tool...',
  //     icon: <Wrench className="h-4 w-4 text-indigo-500" />,
  // },
  handoff_execution_started: {
    title: "Using Tool",
    description: "Executing tool...",
    icon: <Wrench className="h-4 w-4 text-indigo-500" />,
  },
};

const ChatInputLoader = () => {
  return (
    <div className="flex flex-col items-center justify-center py-2">
      <div className="flex space-x-1">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="h-2 w-2 rounded-full bg-gray-400"
            style={{
              animation: `bounce 1s infinite ${i * 0.2}s`,
            }}
          />
        ))}
      </div>
      <style>
        {`
                    @keyframes bounce {
                        0%, 100% { transform: translateY(0); }
                        50% { transform: translateY(-5px); }
                    }
                `}
      </style>
    </div>
  );
};
const StepItem = ({ step }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const shouldTruncate =
    step.details && step.details.length > MAX_DESCRIPTION_LENGTH;
  const displayDetails =
    shouldTruncate && !isExpanded
      ? step.details?.substring(0, MAX_DESCRIPTION_LENGTH) + "..."
      : step.details;
  const getStatusIcon = () => {
    switch (step.status) {
      case "completed":
        return (
          <CheckCircle2 className="w-5 h-5 text-teal-500" strokeWidth={2} />
        );
      case "in_progress":
        return (
          <Loader2
            className="w-5 h-5 text-teal-500 animate-spin"
            strokeWidth={2}
          />
        );
      case "error":
        return <XCircle className="w-5 h-5 text-red-500" strokeWidth={2} />;
      default:
        return <Circle className="w-5 h-5 text-gray-300" strokeWidth={2} />;
    }
  };

  return (
    <div className="flex flex-col gap-2 animate-in fade-in slide-in-from-left-2">
      <div className="flex gap-3 pb-6">
        <div className="flex flex-col items-center">
          {/* Icon */}
          <div className="flex-shrink-0">{getStatusIcon()}</div>

          {/* Connector line */}
          <div className="w-0.5 flex-1 bg-gray-300 mt-2" />
        </div>

        <div className="flex-1">
          <div className="bg-white rounded-lg border border-gray-200 p-3.5 shadow-sm">
            <h3 className="text-xs text-gray-900 mb-1 font-semibold flex flex-row gap-2">
              {step.icon} {step.title}
            </h3>
            <p className="text-xs text-gray-600 leading-relaxed">
              {step?.description}
            </p>
            {step?.details && (
              <p className="text-xs text-gray-600 leading-relaxed">
                {typeof displayDetails === "object"
                  ? JSON.stringify(displayDetails)
                  : displayDetails}
              </p>
            )}
            {shouldTruncate && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex items-center gap-1 mt-2 text-[10px] text-teal-600 hover:text-teal-700 transition-colors"
              >
                {isExpanded ? (
                  <>
                    <span>Show less</span>
                    <ChevronUp className="w-3 h-3" />
                  </>
                ) : (
                  <>
                    <span>Show more</span>
                    <ChevronDown className="w-3 h-3" />
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
// Pastel color options for session headers
const pastelColors = [
  "from-pink-50 to-rose-50 border-pink-100",
  "from-blue-50 to-cyan-50 border-blue-100",
  "from-purple-50 to-violet-50 border-purple-100",
  "from-green-50 to-emerald-50 border-green-100",
  "from-amber-50 to-yellow-50 border-amber-100",
  "from-teal-50 to-cyan-50 border-teal-100",
  "from-indigo-50 to-blue-50 border-indigo-100",
  "from-orange-50 to-amber-50 border-orange-100",
];

// Get consistent color for each session based on its ID
const getSessionColor = (sessionId) => {
  const hash = sessionId
    .split("")
    .reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return pastelColors[hash % pastelColors.length];
};

const MAX_DESCRIPTION_LENGTH = 50;
const ExecutionHistoryItem = ({ index, query, steps, executionId }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const colorClass = getSessionColor(index);
  // here index is a timestamp
  const dtFromMillis = DateTime.fromMillis(Number(index));
  const localDateTime = dtFromMillis.toLocal();
  const formattedDate = localDateTime.toLocaleString(
    DATE_TIME_FORMAT_WITH_SECONDS
  );

  const scrollToExecutionId = (executionId) => {
    console.log("*** scrollToExecutionId executionId", executionId);
    const executionItem = document.getElementById(`execution-${executionId}`);
    if (executionItem) {
      executionItem.scrollIntoView({ behavior: "smooth" });
    }
  };
  return (
    <div
      className={`rounded-xl border border-gray-200 bg-white overflow-hidden transition-all duration-200 ${
        isExpanded ? "shadow-sm" : "shadow-md"
      }`}
    >
      <button
        onClick={() => {
          setIsExpanded(!isExpanded);
          scrollToExecutionId(executionId);
        }}
        className={`flex w-full items-center justify-between px-4 py-3 bg-gradient-to-r ${colorClass} transition-all hover:opacity-90 hover:cursor-pointer`}
      >
        <div className="flex items-center gap-2 flex-grow-0 overflow-hidden flex-shrink-0 flex-basis-[50%]">
          <div
            className={`p-1.5 rounded-lg ${
              isExpanded
                ? "bg-blue-100 text-blue-600"
                : "bg-gray-200 text-gray-500"
            }`}
          >
            <Sparkles className="w-3.5 h-3.5" />
          </div>
          <div
            className={`text-start text-sm font-medium text-gray-700  max-w-[180px] ${
              isExpanded ? "" : "truncate"
            }`}
          >
            {query}
          </div>
          <span className="text-xs text-gray-400">({steps.length} steps)</span>
        </div>
        <div className="flex items-center gap-2 flex-grow-0 flex-shrink-0 flex-basis-[50%]">
          <span className="text-xs text-gray-400">{formattedDate}</span>
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          )}
        </div>
      </button>
      {isExpanded && (
        <div className="px-4 py-2 space-y-2 border-t border-gray-100 bg-white/30">
          {steps.map((step) => (
            <StepItem key={step.step} step={step} />
          ))}
        </div>
      )}
    </div>
  );
};
const ExecutionPanel = ({ lastMessage, onMessageReceived, selectedFile }) => {
  const [searchParams] = useSearchParams();
  const agentId = base64UrlToUuid(searchParams.get("agentId")) ?? undefined;
  const uploadResources = "";
  const [isConnected, setIsConnected] = useState(false);
  const [currentSteps, setCurrentSteps] = useState([]);
  const [history, setHistory] = useState([]);
  const [activeQuery, setActiveQuery] = useState(null);
  const [executionId, setExecutionId] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [isAgentExecutionEnded, setAgentExecutionEnded] = useState(false);
  const [lastSentMessageId, setLastSentMessageId] = useState(null);
  const [lastAgentId, setLastAgentId] = useState(null);
  const wsRef = useRef(null);
  const bufferRef = useRef([]);
  const streamTimerRef = useRef(null);
  const stepsRef = useRef([]);
  const [token] = useState(getCookie(ACCESS_TOKEN));

  useEffect(() => {
    setSessionId(uuidv4());
  }, []);

  // Sync ref
  useEffect(() => {
    stepsRef.current = currentSteps;
  }, [currentSteps]);

  // Initialize WebSocket (Connection only)
  useEffect(() => {
    if (agentId && lastMessage) {
      connectWebSocket();
    }
  }, [agentId, lastMessage]);

  // Handle Sending Messages
  useEffect(() => {
    const getMessageWithUploadFileName = async (
      lastMessage,
      newExecutionId
    ) => {
      try {
        let messageWithUploadFileName = lastMessage.content;

        if (selectedFile) {
          const formData = new FormData();
          formData.append("file", selectedFile);

          const response = await axios.post(
            `${AGENT_ACTIONS_BASE_URL}/${INTEGRATION_RESOURCES_UPLOAD}?execution_id=${newExecutionId}&integration_name=${
              uploadResources?.integration_name ?? ""
            }`,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );

          if (response?.data) {
            messageWithUploadFileName = `${messageWithUploadFileName}, upload_file_path: ${response.data.message}`;
          }
        }

        return messageWithUploadFileName;
      } catch (error) {
        console.error("File upload failed:", error);
        throw error; // optional: rethrow if caller needs to handle it
      }
    };
    if (
      isConnected &&
      lastMessage &&
      lastMessage.id !== lastSentMessageId &&
      wsRef.current
    ) {
      console.log("Sending message:", lastMessage.content);
      const newExecutionId = lastMessage.executionId;

      // Archive previous execution if exists
      if (activeQuery && stepsRef.current.length > 0) {
        setHistory((prev) => [
          ...prev,
          {
            id: Date.now().toString(),
            query: activeQuery,
            steps: stepsRef.current,
            executionId: executionId,
          },
        ]);
      }

      getMessageWithUploadFileName(lastMessage, newExecutionId).then(
        (message) => {
          if (wsRef.current) {
            wsRef.current.send(
              `message=${encodeURIComponent(
                message
              )}&execution_id=${newExecutionId}`
            );
            setAgentExecutionEnded(false);
            setLastSentMessageId(lastMessage.id);
            setActiveQuery(lastMessage.content);
            setExecutionId(newExecutionId);
            setCurrentSteps([]);
          }
        }
      );
    }
  }, [lastMessage, isConnected, lastSentMessageId, activeQuery]);

  const connectWebSocket = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN && lastAgentId === agentId)
      return;

    if (wsRef.current) {
      wsRef.current.close();
    }

    setLastAgentId(agentId ?? null);
    setTimeout(() => {
      const wsUrl = `wss://forge-backend-x-forge.dev.uniphorecloud.com/ws-proxy/crewai-agents/api/v1/agents/execute/${agentId}?token=${token}&agent_inputs=${encodeAgentInputs}&timezone=America/Los_Angeles&stream=true&enable_memory=true&session_id=${sessionId}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log("Connected to WebSocket");
        //wsLogsRef.current = [];
        setIsConnected(true);
      };

      ws.onmessage = (event) => {
        try {
          const outerData = JSON.parse(event.data);
          const data =
            typeof outerData === "string" ? JSON.parse(outerData) : outerData;
          //wsLogsRef.current.push(data);

          handleWebSocketMessage(data);
        } catch (error) {
          console.error("Error parsing WebSocket message:", error);
        }
      };

      ws.onclose = () => {
        console.log("WebSocket disconnected");
        setIsConnected(false);
      };

      ws.onerror = (error) => {
        console.error("WebSocket error:", error);
      };
    }, 1000);
  };

  const handleWebSocketMessage = (data) => {
    if (data.type === "llm_stream_chunk" && typeof data.chunk === "string") {
      bufferRef.current.push(data.chunk);
      scheduleStream();
      return;
    }

    // Handle Progress Steps
    if (stepsMapping[data.type]) {
      const mapping = stepsMapping[data.type];
      let description = mapping.description;

      // Avoid duplicates for some types if needed, or just append
      // For simplicity, we append new steps or update existing ones

      // If it's a start event, add a new step
      if (data.type.includes("started")) {
        console.log(" data for started", data.type);
        let details = data.task_description || data.description || data.input;

        if (data.type === "handoff_execution_started") {
          details = data.message;
          description = `Handing off to ${data.agent_name}...`;
        }
        if (data.type === "agent_execution_started") {
          //status = 'in_progress';
        }

        setCurrentSteps((prev) => [
          ...prev,
          {
            step: prev.length + 1,
            title:
              data.tool_name ||
              data.name ||
              data.agent_name ||
              data.agent_role ||
              mapping.title,
            description: description,
            icon: mapping.icon,
            status: "in_progress",
            type: data.type,
            details: data.context || details,
          },
        ]);
      }
    }

    if (data.type === "handoff_execution_completed") {
      setCurrentSteps((prev) =>
        prev.map((s) =>
          s.type === "handoff_execution_started" &&
          s.status === "in_progress" &&
          s.title === data.agent_name
            ? { ...s, status: "completed", description: "Handoff completed" }
            : s
        )
      );
    }
    if (data.type === "agent_execution_completed") {
      setCurrentSteps((prev) =>
        prev.map((s) =>
          s.type === "agent_execution_started" &&
          s.status === "in_progress" &&
          s.title === data?.agent_role
            ? {
                ...s,
                status: "completed",
                description: "Agent execution completed",
              }
            : s
        )
      );
    }
    if (data.type === "task_completed") {
      setCurrentSteps((prev) =>
        prev.map((s) =>
          s.type === "task_started" &&
          s.status === "in_progress" &&
          s.title === data.task
            ? {
                ...s,
                status: "completed",
                description: "Task completed",
                details: data.output,
              }
            : s
        )
      );
    }
    // Handle Completion of steps (heuristic)
    if (data.type === "llm_call_completed") {
      setCurrentSteps((prev) =>
        prev.map((s) =>
          s.type === "llm_call_started" && s.status === "in_progress"
            ? {
                ...s,
                status: "completed",
                details: data.response?.content || "Response generated",
              }
            : s
        )
      );
    }

    // Handle Final Response
    if (
      data.type === "final_response" ||
      data.type === "supervisor_execution_completed"
    ) {
      immediateStream();
      //downloadLogs();
      setAgentExecutionEnded(true);
      // Mark all steps as completed
      // TODO here remove all the steps that has status as in_progress

      //setCurrentSteps(prev => prev.filter(s => s.status !== 'in_progress'))

      setCurrentSteps((prev) =>
        prev.map((s) => ({ ...s, status: "completed" }))
      );

      // If there's explicit output in supervisor_execution_completed, ensure it's shown
      if (data.type === "supervisor_execution_completed" && data.output) {
        onMessageReceived(data.output, false, lastMessage?.executionId ?? "");
      }
      // If final_response has raw content and we haven't streamed it (or to be safe)
      if (data.type === "final_response" && data.response?.raw) {
        onMessageReceived(
          data.response.raw,
          false,
          lastMessage?.executionId ?? null
        );
      }
    }

    // Handle Error
    if (data.type === "agent_execution_error") {
      setAgentExecutionEnded(true);
      setCurrentSteps((prev) => {
        // Mark in-progress steps as failed
        const newSteps = prev.map((s) =>
          s.status === "in_progress"
            ? { ...s, status: "failed", description: "Failed" }
            : s
        );

        return [
          ...newSteps,
          {
            step: prev.length + 1,
            title: "Execution Error",
            description: "Process failed",
            icon: <AlertCircle className="h-4 w-4 text-red-600" />,
            status: "failed",
            type: data.type,
            details: data.response,
          },
        ];
      });
      onMessageReceived(
        "Agent could not process your request",
        false,
        lastMessage?.executionId ?? null
      );
    }
  };

  const scheduleStream = () => {
    if (streamTimerRef.current != null) return;

    streamTimerRef.current = window.setTimeout(() => {
      streamTimerRef.current = null;
      if (bufferRef.current.length === 0) return;

      const joined = bufferRef.current.join("");
      bufferRef.current = [];

      onMessageReceived(joined, true);
    }, 50);
  };

  const immediateStream = () => {
    if (streamTimerRef.current) {
      clearTimeout(streamTimerRef.current);
      streamTimerRef.current = null;
    }
    if (bufferRef.current.length > 0) {
      const joined = bufferRef.current.join("");
      bufferRef.current = [];
      onMessageReceived(joined, true);
    }
  };

  return (
    <div className="w-full h-full bg-gradient-to-br from-gray-50 via-white to-gray-50 flex flex-col">
      {/* Header */}
      {/* <div className="px-6 py-4 bg-white/80 backdrop-blur-xl border-b border-gray-200/50 shadow-sm flex justify-between items-center">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#1AA3A1] to-[#47B76E] rounded-xl flex items-center justify-center shadow-lg">
                        <Sparkles className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h2 className="font-semibold text-gray-900">Agent Execution</h2>
                        <div className="flex items-center gap-1.5">
                            <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} />
                            <span className="text-xs text-gray-500">{isConnected ? 'Connected' : 'Disconnected'}</span>
                        </div>
                    </div>
                </div>
            </div> */}

      <Tabs defaultValue="execution" className="h-full flex flex-col">
        <div className="p-4 bg-gradient-to-r from-teal-50 via-cyan-50 to-blue-50 border-b border-gray-200 flex-shrink-0 h-[60px] flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center flex-shrink-0">
                <Bot className="w-4 h-4 text-white" />
              </div>
              <h2 className="text-sm text-gray-900">Agent Execution</h2>
            </div>
          </div>

          {/* <TabsList className="h-8">
            <TabsTrigger value="execution" className="text-xs h-7">
              Execution
            </TabsTrigger>
            <TabsTrigger value="trace" className="text-xs h-7">
              Trace Details
            </TabsTrigger>
          </TabsList> */}
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          <TabsContent
            value="execution"
            className="!mt-0 h-full grow space-y-6 pr-2"
          >
            <ScrollArea className="flex-1 p-4">
              <div className="space-y-6">
                {/* History */}

                <div className="space-y-3">
                  {history.map((item) => (
                    <ExecutionHistoryItem
                      key={item.id}
                      query={item.query}
                      steps={item.steps}
                      index={item.id}
                      executionId={item.executionId}
                    />
                  ))}
                </div>

                {/* Current Execution */}
                {activeQuery && (
                  <div className="space-y-2 animate-in fade-in slide-in-from-bottom-4">
                    <div className="flex items-center gap-2 px-1 py-2">
                      <div className="h-px flex-1 bg-gray-200"></div>
                      <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Current Execution
                      </span>
                      <div className="h-px flex-1 bg-gray-200"></div>
                    </div>
                    {currentSteps.length === 0 && !isAgentExecutionEnded ? (
                      <div className="flex items-center justify-center py-8 text-center border border-dashed border-gray-200 rounded-xl bg-gray-50/50">
                        <div className="flex flex-col items-center gap-2">
                          <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                          <p className="text-sm text-gray-500">
                            Processing request...
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {currentSteps.map((step) => (
                          <StepItem key={step.step} step={step} />
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {!activeQuery && history.length === 0 && (
                  <div className="flex flex-col items-center justify-center h-full py-24 text-center">
                    <div className="text-center max-w-md">
                      {/* Icon with gradient background */}
                      <div className="relative mx-auto mb-6 w-20 h-20">
                        <div className="absolute inset-0 bg-gradient-to-br from-teal-100 via-blue-100 to-purple-100 rounded-2xl opacity-60 blur-xl" />
                        <div className="relative w-full h-full bg-gradient-to-br from-teal-400 via-blue-500 to-purple-500 rounded-2xl flex items-center justify-center">
                          <Zap className="w-10 h-10 text-white" />
                        </div>
                      </div>

                      {/* Title */}
                      <h3 className="text-base text-gray-900 mb-2">
                        Ready to Execute
                      </h3>

                      {/* Subtitle */}
                      <p className="text-xs text-gray-500 leading-relaxed mb-4">
                        Start a conversation with your agent to see the
                        execution process in action. Watch as each step unfolds
                        in real-time with detailed tracking.
                      </p>

                      {/* Feature highlights */}
                      <div className="flex items-center justify-center gap-4 text-xs text-gray-400">
                        <div className="flex items-center gap-1.5">
                          <Sparkles className="w-3.5 h-3.5 text-teal-500" />
                          <span>Real-time tracking</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <CheckCircle2 className="w-3.5 h-3.5 text-teal-500" />
                          <span>Step-by-step view</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
          </TabsContent>

          <TabsContent
            value="trace"
            className="mt-2 h-[100%] flex-1 overflow-hidden"
          >
            {activeQuery || history.length > 0 ? (
              <>
                {/* <AgentExecutionTraceDetails executionId={history.length === 0 ? executionId : history[0].executionId} keepPolling={false} /> */}
              </>
            ) : (
              <div className="flex items-center justify-center h-full py-24 text-center">
                <div className="text-center max-w-md">
                  <h3 className="text-base text-gray-900 mb-2">
                    No execution trace found
                  </h3>
                  <p className="text-xs text-gray-500 leading-relaxed mb-4">
                    Start a conversation with your agent to see the execution
                    trace in action. Watch as each step unfolds in real-time
                    with detailed tracking.
                  </p>
                  <div className="flex items-center justify-center gap-4 text-xs text-gray-400">
                    <div className="flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5 text-teal-500" />
                      <span>Real-time tracking</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
};

export default ExecutionPanel;
